import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

export interface Ticket { id: number; subject: string; status: 'open' | 'answered' | 'closed'; createdAt: string; updatedAt?: string; companyName?: string; messages?: number }
export interface Message { id: number; author: 'customer' | 'admin'; body: string; createdAt: string }
export interface Thread { ticket: Ticket & { userName?: string | null; userEmail?: string | null }; messages: Message[] }

const STATUS: Record<Ticket['status'], string> = { open: 'Laukia atsakymo', answered: 'Atsakyta', closed: 'Uždaryta' };
const when = (iso: string) => new Date(iso).toLocaleString();

export function Messages({ messages, mine }: { messages: Message[]; mine: Message['author'] }) {
  return (
    <div className="support-thread">
      {messages.map((m) => (
        <div key={m.id} className={`support-message ${m.author === mine ? 'is-mine' : ''} support-message--${m.author}`}>
          <p>{m.body}</p><small>{m.author === 'admin' ? 'FakturaSmart Pro' : ''} {when(m.createdAt)}</small>
        </div>
      ))}
    </div>
  );
}

/** Pagalba: the company's requests to FakturaSmart Pro and the answers. */
export function SupportPage({ me, ticketId, onOpen }: { me: Me; ticketId?: number; onOpen: (id?: number) => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['support'], queryFn: () => api.get<{ tickets: Ticket[] }>('api/v1/support').then((r) => r.tickets) });
  const thread = useQuery({ queryKey: ['support', ticketId], enabled: Boolean(ticketId), queryFn: () => api.get<Thread>(`api/v1/support/${ticketId}`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['support'] });
  const create = useMutation({
    mutationFn: (body: { subject: string; message: string }) => api.post<{ ticket: { id: number } }>('api/v1/support', body),
    onSuccess: async (r) => { await refresh(); onOpen(r.ticket.id); },
  });
  const answer = useMutation({ mutationFn: (message: string) => api.post(`api/v1/support/${ticketId}/messages`, { message }), onSuccess: refresh });
  const close = useMutation({ mutationFn: () => api.post(`api/v1/support/${ticketId}/close`), onSuccess: refresh });
  const [draft, setDraft] = useState('');
  const error = create.error ?? answer.error ?? close.error;
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Paskyra')} title={t('Pagalba')} text={t('Parašykite mums — atsakysime el. paštu ir čia.')} />}>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <div className="support-layout">
        <aside className="panel-card">
          <button type="button" className="button button--primary support-new" onClick={() => onOpen()}>+ {t('Nauja užklausa')}</button>
          <div className="support-list">
            {list.isSuccess && !list.data.length ? <p className="muted">{t('Užklausų dar nėra.')}</p> : null}
            {list.data?.map((x) => (
              <button key={x.id} type="button" className={`support-item ${x.id === ticketId ? 'active' : ''}`} onClick={() => onOpen(x.id)}>
                <strong>{x.subject}</strong><small><span className={`badge badge--support-${x.status}`}>{t(STATUS[x.status])}</span> {new Date(x.updatedAt ?? x.createdAt).toLocaleDateString()}</small>
              </button>
            ))}
          </div>
        </aside>
        <section className="panel-card">
          {ticketId && thread.data ? (
            <>
              <div className="card-head"><div><p className="eyebrow">#{thread.data.ticket.id} · {t(STATUS[thread.data.ticket.status])}</p><h2>{thread.data.ticket.subject}</h2></div>
                {thread.data.ticket.status !== 'closed' ? <button type="button" className="button button--ghost button--small" onClick={() => close.mutate()}>{t('Uždaryti užklausą')}</button> : null}</div>
              <Messages messages={thread.data.messages} mine="customer" />
              <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (draft.trim()) answer.mutate(draft.trim(), { onSuccess: () => setDraft('') }); }}>
                <label>{t('Atsakyti')}<textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} required /></label>
                <div className="form-actions"><button className="button button--primary" disabled={answer.isPending}>{t('Siųsti')}</button></div>
              </form>
            </>
          ) : (
            <form onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              const d = new FormData(e.currentTarget);
              create.mutate({ subject: String(d.get('subject')), message: String(d.get('message')) });
            }}>
              <p className="eyebrow">{t('Nauja užklausa')}</p><h2>{t('Kuo galime padėti?')}</h2>
              <label>{t('Tema')}<input name="subject" maxLength={200} required /></label>
              <label>{t('Žinutė')}<textarea name="message" rows={6} maxLength={5000} required /></label>
              <div className="form-actions"><button className="button button--primary" disabled={create.isPending}>{t('Siųsti')}</button></div>
            </form>
          )}
        </section>
      </div>
    </PanelShell>
  );
}
